import { useState, FormEvent } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { verifyTOTPLogin } from '../services/mfaService';
import { AppFooter } from '../components/AppFooter';
import { getErrorMessage } from '../utils/getErrorMessage';
import '../styles/LoginPage.css';
import logo from '../assets/logo.png';

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA state
  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call API directly to check for mustChangePassword and MFA
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, password })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Login failed');
      }

      // Check if password change is required
      if (result.data.mustChangePassword) {
        // Redirect to change password page with user info
        navigate('/change-password', { state: { user: result.data.user } });
        return;
      }

      // Check if MFA is required
      if (result.data.requiresMFA) {
        // Show MFA verification form
        setShowMFA(true);
        setLoading(false);
        return;
      }

      // Normal login flow - store token and user directly
      if (result.data.token && result.data.user) {
        localStorage.setItem('tacolcy_token', result.data.token);
        localStorage.setItem('tacolcy_user', JSON.stringify(result.data.user));
        window.location.href = '/dashboard'; // Force full page reload to update auth state
      } else {
        throw new Error('No token received');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Invalid email or password. Please try again.'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyTOTPLogin(email, mfaCode, useBackupCode);

      if (result.token) {
        // Prefer the full user object the backend now returns; fall back to a
        // minimal email-only record only if an older API build is in play.
        const user = result.user ?? { email };

        localStorage.setItem('tacolcy_token', result.token);
        localStorage.setItem('tacolcy_user', JSON.stringify(user));

        if (result.warning) {
          // Show warning about backup code usage
          alert(result.warning);
        }

        window.location.href = '/dashboard';
      } else {
        throw new Error('No token received from MFA verification');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Invalid verification code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShowMFA(false);
    setMfaCode('');
    setUseBackupCode(false);
    setError('');
  };

  // MFA Verification Form
  if (showMFA) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
            <h2 className="login-title">Two-Factor Authentication</h2>
            <p style={{ textAlign: 'center', color: '#666', fontSize: '0.95rem' }}>
              {useBackupCode
                ? 'Enter one of your backup codes'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleMFASubmit} className="login-form">
            {error && (
              <div className="login-error">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="mfaCode">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </label>
              <input
                type="text"
                id="mfaCode"
                value={mfaCode}
                onChange={(e) => setMfaCode(
                  useBackupCode
                    ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                    : e.target.value.replace(/\D/g, '').slice(0, 6)
                )}
                required
                placeholder={useBackupCode ? 'XXXXXXXXXX' : '000000'}
                maxLength={useBackupCode ? 10 : 6}
                autoComplete="off"
                autoFocus
                disabled={loading}
                style={{
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: useBackupCode ? '2px' : '10px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <button
              type="submit"
              className="button-primary login-button"
              disabled={loading || (useBackupCode ? mfaCode.length !== 10 : mfaCode.length !== 6)}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setUseBackupCode(!useBackupCode)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#5B2C86',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.9rem'
                }}
              >
                {useBackupCode
                  ? 'Use authenticator app instead'
                  : 'Use a backup code instead'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
              <button
                type="button"
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                ← Back to login
              </button>
            </div>

            <div className="login-footer">
              <p className="login-help">
                For assistance, contact your system administrator
              </p>
            </div>
          </form>
        </div>

        <AppFooter />
      </div>
    );
  }

  // Regular Login Form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logo} alt="Belafonte TACOLCY Center" className="login-logo" />
          <h2 className="login-title">Client Relationship Management System</h2>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your.email@tacolcy.org"
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="button-primary login-button"
            disabled={loading || isLoading}
          >
            {loading || isLoading ? 'Signing in...' : 'Sign In'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Link
              to="/forgot-password"
              style={{
                color: '#5B2C86',
                textDecoration: 'none',
                fontSize: '0.95rem'
              }}
            >
              Forgot Password?
            </Link>
          </div>

          <div className="login-footer">
            <p className="login-help">
              For assistance, contact your system administrator
            </p>
          </div>
        </form>
      </div>

      <AppFooter />
    </div>
  );
}
